// src/redux/profileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchUserProfile as fetchUserProfileService, // GET /profile
  createUserProfile as createUserProfileService, // POST /profile
  updateUserProfile as updateUserProfileService, // PUT /profile
  fetchProfileApiUsage as fetchProfileApiUsageService, // GET /profile/api-usage (optional)
} from "../services/profileService";

/**
 * Normalize API errors coming back from lambda patterns:
 * - { error, message, details }
 * - string
 * - thrown Error
 */
const toErrorPayload = (err, fallbackMessage) => {
  if (!err) return { message: fallbackMessage };

  if (typeof err === "string") return { message: err };

  // fetch-like errors (your apiService throws Error(message))
  const message =
    err?.message ||
    err?.error ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    fallbackMessage;

  const details =
    err?.details ||
    err?.response?.data?.details ||
    err?.response?.data?.errors ||
    null;

  return { message, details };
};

/**
 * New backend expects/returns camelCase profile objects.
 * Lambdas may return:
 * - GET /profile -> profile object
 * - POST /profile -> { message } OR profile object
 * - PUT /profile -> { message } OR profile object
 */
const looksLikeProfile = (obj) => {
  if (!obj || typeof obj !== "object") return false;
  return "familyName" in obj || "birthdate" in obj || "accountType" in obj;
};

const unwrapProfile = (res) => {
  if (looksLikeProfile(res)) return res;
  if (looksLikeProfile(res?.profile)) return res.profile;
  if (looksLikeProfile(res?.user)) return res.user;
  return null;
};

// Fetch user profile (GET /profile)
export const fetchUserProfileAsync = createAsyncThunk(
  "profile/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchUserProfileService();
      return unwrapProfile(res);
    } catch (error) {
      return rejectWithValue(
        toErrorPayload(error, "Failed to fetch profile information.")
      );
    }
  }
);

// Create user profile (POST /profile)
export const createUserProfileAsync = createAsyncThunk(
  "profile/createUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const res = await createUserProfileService(profileData);

      // if backend returns profile directly, use it
      const created = unwrapProfile(res);
      if (created) return created;

      // ✅ IMPORTANT FIX:
      // backend returns { message }, so don't re-fetch immediately (avoids 404 + eventual consistency)
      // store what we just submitted as the current profile
      return profileData;
    } catch (error) {
      return rejectWithValue(toErrorPayload(error, "Failed to create profile."));
    }
  }
);

// Update user profile (PUT /profile)
export const updateUserProfileAsync = createAsyncThunk(
  "profile/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const res = await updateUserProfileService(profileData);

      // if backend returns updated profile, accept it
      const updated = unwrapProfile(res);
      if (updated) return updated;

      // otherwise fetch latest
      const latest = await fetchUserProfileService();
      return unwrapProfile(latest);
    } catch (error) {
      return rejectWithValue(
        toErrorPayload(error, "Failed to update profile information.")
      );
    }
  }
);

// Fetch API usage (GET /profile/api-usage) — launch-safe default
export const fetchProfileApiUsageAsync = createAsyncThunk(
  "profile/fetchProfileApiUsage",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchProfileApiUsageService();
      const usage = data?.api_usage || data || null;

      return {
        monthly_limit: usage?.monthly_limit ?? 0,
        current_usage: usage?.current_usage ?? 0,
        resets_on: usage?.resets_on ?? null,
        api_key: usage?.api_key ?? null,
      };
    } catch (error) {
      // Launch-safe: don’t break the page if not deployed
      return {
        monthly_limit: 0,
        current_usage: 0,
        resets_on: null,
        api_key: null,
      };

      // If you prefer hard failure:
      // return rejectWithValue(toErrorPayload(error, "Failed to fetch API usage."));
    }
  }
);

const initialState = {
  profile: null,

  loading: false,
  error: null, // { message, details } | null

  apiUsage: null,
  apiUsageLoading: false,
  apiUsageError: null, // { message, details } | null
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfileErrors(state) {
      state.error = null;
      state.apiUsageError = null;
    },
    clearProfileState(state) {
      state.profile = null;
      state.loading = false;
      state.error = null;

      state.apiUsage = null;
      state.apiUsageLoading = false;
      state.apiUsageError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch profile
      .addCase(fetchUserProfileAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfileAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload || null;
      })
      .addCase(fetchUserProfileAsync.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || { message: "Failed to fetch profile information." };
      })

      // Create profile
      .addCase(createUserProfileAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserProfileAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload || null;
      })
      .addCase(createUserProfileAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { message: "Failed to create profile." };
      })

      // Update profile
      .addCase(updateUserProfileAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfileAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload || null;
      })
      .addCase(updateUserProfileAsync.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || { message: "Failed to update profile information." };
      })

      // API usage
      .addCase(fetchProfileApiUsageAsync.pending, (state) => {
        state.apiUsageLoading = true;
        state.apiUsageError = null;
      })
      .addCase(fetchProfileApiUsageAsync.fulfilled, (state, action) => {
        state.apiUsageLoading = false;
        state.apiUsage =
          action.payload || {
            monthly_limit: 0,
            current_usage: 0,
            resets_on: null,
            api_key: null,
          };
      })
      .addCase(fetchProfileApiUsageAsync.rejected, (state, action) => {
        state.apiUsageLoading = false;
        state.apiUsageError =
          action.payload || { message: "Failed to fetch API usage." };
      });
  },
});

export const { clearProfileErrors, clearProfileState } = profileSlice.actions;

// Selectors (✅ fixed: return profile object from slice state)
export const selectUserProfile = (state) => state.profile?.profile ?? null;

export const selectProfileLoading = (state) => state.profile?.loading || false;

// Return user-friendly message for UI
export const selectProfileError = (state) => {
  const e = state.profile?.error || null;
  if (!e) return null;
  return typeof e === "string" ? e : e.message || "Request failed.";
};

export const selectProfileErrorDetails = (state) => {
  const e = state.profile?.error || null;
  if (!e || typeof e === "string") return null;
  return e.details || null;
};

export const selectApiUsage = (state) => state.profile?.apiUsage || null;
export const selectApiUsageLoading = (state) =>
  state.profile?.apiUsageLoading || false;

export const selectApiUsageError = (state) => {
  const e = state.profile?.apiUsageError || null;
  if (!e) return null;
  return typeof e === "string" ? e : e.message || "Request failed.";
};

export default profileSlice.reducer;
